<template>
  <div class="register-team">
    <XNav :content="[{ name: $t('registerTeam.title') }]" />

    <div class="content" v-if="!submitted">
      <h1>{{ $t("registerTeam.heading") }}</h1>
      <p class="intro">{{ $t("registerTeam.intro") }}</p>

      <div class="leagues-section" v-if="registrationSettings.enabled && registrationSettings.leagues && registrationSettings.leagues.length">
        <h2>{{ $t("registerTeam.availableLeagues") }}</h2>
        <div class="leagues-cards">
          <div class="league-card" v-for="(league, i) in registrationSettings.leagues" :key="i">
            <h3 class="league-card-name">{{ league.name }}</h3>
            <div class="league-card-details">
              <div class="league-detail" v-if="league.type">
                <span class="detail-label">{{ $t("registerTeam.indoorOutdoor") }}</span>
                <span class="detail-value">{{ league.type }}</span>
              </div>
              <div class="league-detail" v-if="league.field">
                <span class="detail-label">{{ $t("registerTeam.primaryField") }}</span>
                <span class="detail-value">{{ league.field }}</span>
              </div>
              <div class="league-detail" v-if="league.startDate">
                <span class="detail-label">{{ $t("registerTeam.startDate") }}</span>
                <span class="detail-value">{{ league.startDate }}</span>
              </div>
              <div class="league-detail" v-if="league.playingTimes">
                <span class="detail-label">{{ $t("registerTeam.playingTimes") }}</span>
                <span class="detail-value">{{ league.playingTimes }}</span>
              </div>
              <div class="league-detail" v-if="league.divisions">
                <span class="detail-label">{{ $t("registerTeam.divisionsLabel") }}</span>
                <span class="detail-value">{{ league.divisions }}</span>
              </div>
              <div class="league-detail" v-if="league.format">
                <span class="detail-label">{{ $t("registerTeam.formatLabel") }}</span>
                <span class="detail-value">{{ league.format }}</span>
              </div>
              <div class="league-detail" v-if="league.price">
                <span class="detail-label">{{ $t("registerTeam.priceLabel") }}</span>
                <span class="detail-value league-price">{{ league.price }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form class="form" @submit.prevent="submit">
        <div class="form-group">
          <label for="teamName">{{ $t("registerTeam.teamName") }} *</label>
          <input id="teamName" type="text" v-model="form.teamName" required />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="firstName">{{ $t("common.firstname") }} *</label>
            <input id="firstName" type="text" v-model="form.captainFirstName" required />
          </div>
          <div class="form-group">
            <label for="lastName">{{ $t("common.lastname") }} *</label>
            <input id="lastName" type="text" v-model="form.captainLastName" required />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="email">{{ $t("common.email") }} *</label>
            <input id="email" type="email" v-model="form.captainEmail" required />
          </div>
          <div class="form-group">
            <label for="phone">{{ $t("common.phone_number") }} *</label>
            <input id="phone" type="tel" v-model="form.captainPhone" required />
          </div>
        </div>

        <div class="form-group">
          <label for="hearAboutUs">{{ $t("registerTeam.hearAboutUs") }}</label>
          <select id="hearAboutUs" v-model="form.hearAboutUs">
            <option value="">{{ $t("registerTeam.select") }}</option>
            <option value="Social Media">{{ $t("registerTeam.sources.socialMedia") }}</option>
            <option value="Google Search">{{ $t("registerTeam.sources.google") }}</option>
            <option value="Friend/Word of Mouth">{{ $t("registerTeam.sources.friend") }}</option>
            <option value="Played Before">{{ $t("registerTeam.sources.playedBefore") }}</option>
            <option value="Other">{{ $t("registerTeam.sources.other") }}</option>
          </select>
        </div>

        <div class="form-group">
          <label for="notes">{{ $t("registerTeam.notes") }}</label>
          <textarea id="notes" v-model="form.notes" rows="3" :placeholder="$t('registerTeam.notesPlaceholder')"></textarea>
        </div>

        <p class="error" v-if="error">{{ error }}</p>

        <button type="submit" class="submit-btn" :disabled="loading">
          {{ loading ? $t("registerTeam.submitting") : $t("registerTeam.submit") }}
        </button>
      </form>

      <div class="faq-section">
        <h2>{{ $t("registerTeam.faqHeading") }}</h2>
        <div class="faq-item" v-for="(item, i) in faqItems" :key="i">
          <p class="faq-q">{{ item.q }}</p>
          <p class="faq-a">{{ item.a }}</p>
        </div>
      </div>
    </div>

    <div class="success" v-else>
      <div class="success-card">
        <h2>{{ $t("registerTeam.successTitle") }}</h2>
        <p>{{ $t("registerTeam.successMessage") }}</p>
        <button @click="$router.push('/')">{{ $t("payment.return_home") }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import XNav from "@/components/XNav.vue";

export default {
  name: "RegisterTeam",
  components: { XNav },
  metaInfo() {
    return {
      title: this.$t("seo_metadata.register_team.title"),
      meta: [
        { vmid: "description", name: "description", content: this.$t("seo_metadata.register_team.description") },
        { vmid: "keywords", name: "keywords", content: this.$t("seo_metadata.keywords") },
        { vmid: "og:title", property: "og:title", content: this.$t("seo_metadata.register_team.title") },
        { vmid: "og:description", property: "og:description", content: this.$t("seo_metadata.register_team.description") },
        { vmid: "og:url", property: "og:url", content: "https://kingsleague.ca/register-team" },
        { vmid: "og:type", property: "og:type", content: "website" },
        { vmid: "twitter:title", name: "twitter:title", content: this.$t("seo_metadata.register_team.title") },
        { vmid: "twitter:description", name: "twitter:description", content: this.$t("seo_metadata.register_team.description") }
      ],
      link: [{ rel: "canonical", href: "https://kingsleague.ca/register-team" }],
      script: [{
        type: "application/ld+json",
        json: {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Register Your Team - The Kings League",
          "description": "Register your team for Montreal and Laval's premier adult soccer league.",
          "url": "https://kingsleague.ca/register-team",
          "isPartOf": {
            "@type": "WebSite",
            "name": "The Kings League",
            "url": "https://kingsleague.ca"
          }
        }
      }]
    };
  },
  data() {
    return {
      form: {
        teamName: "",
        captainFirstName: "",
        captainLastName: "",
        captainEmail: "",
        captainPhone: "",
        hearAboutUs: "",
        notes: "",
      },
      submitted: false,
      loading: false,
      error: "",
      registrationSettings: { enabled: false, leagues: [] },
    };
  },
  created() {
    axios.get(this.$apiUrl + "/config/team-registration").then((res) => {
      this.registrationSettings = res.data;
    });
  },
  computed: {
    faqItems() {
      return [
        { q: this.$t("registerTeam.faq.q1"), a: this.$t("registerTeam.faq.a1") },
        { q: this.$t("registerTeam.faq.q2"), a: this.$t("registerTeam.faq.a2") },
        { q: this.$t("registerTeam.faq.q3"), a: this.$t("registerTeam.faq.a3") },
        { q: this.$t("registerTeam.faq.q4"), a: this.$t("registerTeam.faq.a4") },
        { q: this.$t("registerTeam.faq.q5"), a: this.$t("registerTeam.faq.a5") },
      ];
    },
  },
  methods: {
    async submit() {
      this.error = "";
      this.loading = true;
      try {
        await axios.post(this.$apiUrl + "/registration-request", this.form);
        this.submitted = true;
      } catch (err) {
        if (err.response && err.response.status === 429) {
          this.error = this.$t("registerTeam.errorRateLimit");
        } else {
          this.error = this.$t("error.unexpected");
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/theme.scss";

.register-team {
  padding-bottom: 30px;
}

.content {
  max-width: 900px;
  margin: 0 auto;
}

.leagues-section {
  margin-bottom: 30px;

  h2 {
    @extend %font-bold;
    font-size: 22px;
    margin-bottom: 15px;
  }
}
.leagues-cards {
  display: flex;
  gap: 15px;
  overflow-x: auto;
}
.league-card {
  flex: 1;
  min-width: 220px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}
.league-card-name {
  @extend %font-bold;
  background-color: $secondary;
  color: white;
  padding: 12px 15px;
  font-size: 16px;
  text-align: center;
}
.league-card-details {
  padding: 5px 0;
}
.league-detail {
  display: flex;
  flex-direction: column;
  padding: 8px 15px;
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    border-bottom: none;
  }
}
.detail-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #999;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}
.detail-value {
  font-size: 14px;
  color: #333;
}
.league-price {
  @extend %font-bold;
  color: $secondary;
}

h1 {
  @extend %font-bold;
  font-size: 28px;
  margin: 20px 0 10px;
}

.intro {
  font-size: 16px;
  color: #555;
  margin-bottom: 25px;
  line-height: 1.5;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-row {
  display: flex;
  gap: 15px;
  & > .form-group {
    flex: 1;
  }
}

.form-group {
  display: flex;
  flex-direction: column;

  label {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 5px;
    color: #333;
  }

  input, select, textarea {
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    font-size: 15px;
    font-family: inherit;
    &:focus {
      outline: none;
      border-color: $secondary;
    }
  }

  textarea {
    resize: vertical;
  }
}

.error {
  color: red;
  font-weight: bold;
  text-align: center;
}

.submit-btn {
  @extend %secondary-pattern;
  padding: 12px;
  border: none;
  border-radius: 5px;
  font-size: 18px;
  color: white;
  cursor: pointer;
  margin-top: 10px;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.faq-section {
  margin-top: 40px;
  border-top: 1px solid #eee;
  padding-top: 20px;

  h2 {
    @extend %font-bold;
    font-size: 22px;
    margin-bottom: 15px;
  }
}

.faq-item {
  margin-bottom: 15px;
}
.faq-q {
  @extend %font-bold;
  font-size: 16px;
  margin-bottom: 3px;
}
.faq-a {
  font-size: 15px;
  color: #555;
  line-height: 1.4;
}

.success {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
}

.success-card {
  text-align: center;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 2px 20px rgba(0,0,0,0.1);

  h2 {
    @extend %font-bold;
    color: $secondary;
    font-size: 24px;
    margin-bottom: 10px;
  }
  p {
    font-size: 16px;
    margin-bottom: 20px;
    color: #555;
  }
  button {
    @extend %secondary-pattern;
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    color: white;
    font-size: 16px;
    cursor: pointer;
  }
}

@media only screen and (max-width: $mobile-breakpoint) {
  .form-row {
    flex-direction: column;
  }
  h1 {
    font-size: 22px;
  }
}
</style>
